import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, CircularProgress, Select,
  MenuItem, InputLabel, FormControl, Table, TableBody, TableCell,
  TableHead, TableRow, Divider,
} from '@mui/material';
import { Add, Edit, Delete, BedOutlined, MeetingRoom } from '@mui/icons-material';
import { wardService, bedService } from '../../services/api';
import TopBar from '../../components/TopBar';

const WARD_TYPES  = ['General','ICU','Private','SemiPrivate','NICU','Emergency','Maternity','Pediatric'];
const BED_TYPES   = ['General','Private','ICU','Deluxe','SemiPrivate'];
const BED_STATUSES = ['Available','Reserved','UnderMaintenance','Housekeeping'];

const STATUS_COLORS = {
  Available:        { bg: '#e8f5e9', color: '#2e7d32' },
  Occupied:         { bg: '#ffebee', color: '#c62828' },
  Reserved:         { bg: '#fff8e1', color: '#f57f17' },
  UnderMaintenance: { bg: '#f5f5f5', color: '#757575' },
  Housekeeping:     { bg: '#e3f2fd', color: '#1565c0' },
};

const emptyWard = { name: '', wardType: 'General', floor: '', description: '', isActive: true };
const emptyBed  = { wardId: 0, bedNumber: '', bedType: 'General', status: 'Available', isActive: true };

const WardManagementPage = () => {
  const navigate = useNavigate();
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(false);

  // Ward dialog
  const [wardDialog, setWardDialog] = useState(false);
  const [editingWard, setEditingWard] = useState(null);
  const [wardForm, setWardForm] = useState(emptyWard);
  const [wardSaving, setWardSaving] = useState(false);
  const [wardError, setWardError] = useState('');

  // Bed management dialog
  const [bedDialog, setBedDialog] = useState({ open: false, ward: null });
  const [wardBeds, setWardBeds] = useState([]);
  const [bedsLoading, setBedsLoading] = useState(false);
  const [bedForm, setBedForm] = useState(emptyBed);
  const [editingBed, setEditingBed] = useState(null);
  const [addBedOpen, setAddBedOpen] = useState(false);
  const [bedSaving, setBedSaving] = useState(false);
  const [bedError, setBedError] = useState('');

  const loadWards = useCallback(async () => {
    setLoading(true);
    try { const r = await wardService.getAll(); setWards(r.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWards(); }, [loadWards]);

  // ── Ward CRUD ──
  const openAddWard = () => { setEditingWard(null); setWardForm(emptyWard); setWardError(''); setWardDialog(true); };
  const openEditWard = (w) => {
    setEditingWard(w);
    setWardForm({ name: w.name, wardType: w.wardType, floor: w.floor || '', description: w.description || '', isActive: w.isActive });
    setWardError('');
    setWardDialog(true);
  };
  const handleWardSave = async (e) => {
    e.preventDefault();
    if (!wardForm.name.trim()) { setWardError('Ward name is required.'); return; }
    setWardSaving(true);
    try {
      if (editingWard) await wardService.update(editingWard.id, wardForm);
      else await wardService.create(wardForm);
      setWardDialog(false);
      loadWards();
    } catch (err) { setWardError(err.response?.data?.message || 'Failed to save ward'); }
    finally { setWardSaving(false); }
  };
  const handleWardDelete = async (w) => {
    if (!window.confirm(`Delete ward "${w.name}"? All beds in it will also be removed.`)) return;
    try { await wardService.delete(w.id); loadWards(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete ward'); }
  };

  // ── Bed Management ──
  const openBedDialog = async (ward) => {
    setBedDialog({ open: true, ward });
    setBedsLoading(true);
    setAddBedOpen(false);
    try {
      const r = await wardService.getBeds(ward.id);
      setWardBeds(r.data.beds || []);
    } catch { setWardBeds([]); }
    finally { setBedsLoading(false); }
  };
  const refreshBeds = async () => {
    if (!bedDialog.ward) return;
    const r = await wardService.getBeds(bedDialog.ward.id);
    setWardBeds(r.data.beds || []);
    loadWards(); // refresh ward counts
  };
  const openAddBed = () => {
    setEditingBed(null);
    setBedForm({ ...emptyBed, wardId: bedDialog.ward?.id || 0 });
    setBedError('');
    setAddBedOpen(true);
  };
  const openEditBed = (bed) => {
    setEditingBed(bed);
    setBedForm({ wardId: bed.wardId, bedNumber: bed.bedNumber, bedType: bed.bedType, status: bed.status, isActive: bed.isActive });
    setBedError('');
    setAddBedOpen(true);
  };
  const handleBedSave = async (e) => {
    e.preventDefault();
    if (!bedForm.bedNumber.trim()) { setBedError('Bed number is required.'); return; }
    setBedSaving(true);
    try {
      if (editingBed) await bedService.update(editingBed.id, bedForm);
      else await bedService.create(bedForm);
      setAddBedOpen(false);
      refreshBeds();
    } catch (err) { setBedError(err.response?.data?.message || 'Failed to save bed'); }
    finally { setBedSaving(false); }
  };
  const handleBedDelete = async (bed) => {
    if (!window.confirm(`Delete bed "${bed.bedNumber}"?`)) return;
    try { await bedService.delete(bed.id); refreshBeds(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete bed'); }
  };
  const handleBedStatus = async (bed, newStatus) => {
    try { await bedService.updateStatus(bed.id, newStatus); refreshBeds(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to update status'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Ward & Bed Management" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Define wards and manage beds. Beds can be set to <strong>Available, Reserved, Under Maintenance</strong> or <strong>Housekeeping</strong> by admin. <strong>Occupied</strong> is set automatically on patient admission.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAddWard} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
            Add Ward
          </Button>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Ward Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Floor</strong></TableCell>
                  <TableCell align="center"><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>Available</strong></TableCell>
                  <TableCell align="center"><strong>Occupied</strong></TableCell>
                  <TableCell align="center"><strong>Reserved</strong></TableCell>
                  <TableCell width={100}><strong>Status</strong></TableCell>
                  <TableCell width={160}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wards.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No wards found. Add a ward to get started.</TableCell></TableRow>
                )}
                {wards.map(w => (
                  <TableRow key={w.id} hover>
                    <TableCell><Typography fontWeight={600}>{w.name}</Typography></TableCell>
                    <TableCell><Chip size="small" label={w.wardType} variant="outlined" /></TableCell>
                    <TableCell>{w.floor || '—'}</TableCell>
                    <TableCell align="center"><Typography variant="body2" fontWeight={600}>{w.totalBeds}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>{w.available}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>{w.occupied}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="body2" sx={{ color: '#f57f17', fontWeight: 600 }}>{w.reserved}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={w.isActive ? 'Active' : 'Inactive'} color={w.isActive ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Manage Beds">
                        <IconButton size="small" color="primary" onClick={() => openBedDialog(w)}>
                          <BedOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Ward">
                        <IconButton size="small" onClick={() => openEditWard(w)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Ward">
                        <IconButton size="small" color="error" onClick={() => handleWardDelete(w)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      {/* Add / Edit Ward Dialog */}
      <Dialog open={wardDialog} onClose={() => setWardDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWard ? 'Edit Ward' : 'Add Ward'}</DialogTitle>
        <form onSubmit={handleWardSave}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {wardError && <Typography color="error">{wardError}</Typography>}
            <TextField required fullWidth label="Ward Name" value={wardForm.name}
              onChange={e => setWardForm(p => ({ ...p, name: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Ward Type</InputLabel>
                <Select value={wardForm.wardType} label="Ward Type" onChange={e => setWardForm(p => ({ ...p, wardType: e.target.value }))}>
                  {WARD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Floor" value={wardForm.floor}
                placeholder="e.g. Ground Floor, Floor 1"
                onChange={e => setWardForm(p => ({ ...p, floor: e.target.value }))} />
            </Box>
            <TextField fullWidth label="Description" multiline rows={2} value={wardForm.description}
              onChange={e => setWardForm(p => ({ ...p, description: e.target.value }))} />
            <FormControlLabel control={<Switch checked={wardForm.isActive} onChange={e => setWardForm(p => ({ ...p, isActive: e.target.checked }))} color="success" />} label="Active" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWardDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={wardSaving}>{wardSaving ? 'Saving…' : editingWard ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Bed Management Dialog */}
      <Dialog open={bedDialog.open} onClose={() => setBedDialog({ open: false, ward: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoom color="primary" />
              <Typography variant="h6">Beds — {bedDialog.ward?.name}</Typography>
            </Box>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={openAddBed}>Add Bed</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Add / Edit bed inline form */}
          {addBedOpen && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {editingBed ? 'Edit Bed' : 'Add New Bed'}
              </Typography>
              {bedError && <Typography color="error" sx={{ mb: 1 }}>{bedError}</Typography>}
              <form onSubmit={handleBedSave}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <TextField required size="small" label="Bed Number" value={bedForm.bedNumber} sx={{ width: 140 }}
                    placeholder="e.g. G-01, ICU-1"
                    onChange={e => setBedForm(p => ({ ...p, bedNumber: e.target.value }))} />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Bed Type</InputLabel>
                    <Select value={bedForm.bedType} label="Bed Type" onChange={e => setBedForm(p => ({ ...p, bedType: e.target.value }))}>
                      {BED_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControlLabel control={<Switch size="small" checked={bedForm.isActive} onChange={e => setBedForm(p => ({ ...p, isActive: e.target.checked }))} color="success" />} label="Active" />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="small" disabled={bedSaving}>{bedSaving ? 'Saving…' : editingBed ? 'Update' : 'Add Bed'}</Button>
                  <Button size="small" onClick={() => setAddBedOpen(false)}>Cancel</Button>
                </Box>
              </form>
            </Paper>
          )}

          {bedsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : wardBeds.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No beds in this ward. Click "Add Bed" to create beds.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Bed No.</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Current Patient</strong></TableCell>
                  <TableCell><strong>Change Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wardBeds.map(bed => {
                  const sc = STATUS_COLORS[bed.status] || {};
                  const patient = bed.currentAdmission?.patient;
                  return (
                    <TableRow key={bed.id} hover>
                      <TableCell><Typography fontWeight={600}>{bed.bedNumber}</Typography></TableCell>
                      <TableCell>{bed.bedType}</TableCell>
                      <TableCell>
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, display: 'inline-block', bgcolor: sc.bg, color: sc.color, fontSize: '0.75rem', fontWeight: 600 }}>
                          {bed.status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {patient ? (
                          <Typography variant="body2">{patient.firstName} {patient.lastName} <Typography component="span" variant="caption" color="text.secondary">(Day {bed.currentAdmission?.daysInHospital})</Typography></Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {bed.status !== 'Occupied' && (
                          <Select size="small" value={bed.status} sx={{ fontSize: '0.75rem', height: 28 }}
                            onChange={e => handleBedStatus(bed, e.target.value)}>
                            {BED_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>)}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditBed(bed)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={bed.status === 'Occupied' ? 'Cannot delete occupied bed' : 'Delete'}>
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleBedDelete(bed)} disabled={bed.status === 'Occupied'}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBedDialog({ open: false, ward: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WardManagementPage;
